import React from "react";
import BottomSheet from "./BottomSheet";
import NumberPad from "./NumberPad";
import ShopPlaylists from "./ShopPlaylists";
import SetlistSealedArt from "./SetlistSealedArt";
import { PROFILE_ICONS } from "./profileMenuIcons";
import { FieldStatus, type SaveState } from "./FieldStatus";
import { currencySymbol, formatAmountForCopy } from "../utils/currency";
import { PayinCurrency, setlistChipsFor } from "../utils/payinConfig";
import { SUFFIX_CUR } from "../utils/requestsFormat";
import type { User } from "../types/User";
import type { Storefront } from "../hooks/useStorefront";
import { t } from "../i18n";
import { T } from "../i18n/T";
import "./StorefrontSheets.css";

// The Storefront drawers on the Profile page: the Sell Merch teaser, the Sell
// Playlists sheet (v1 two-card layout / v2 one-card + shelf) with its number
// pad, and the "See what fans get" sample page. Split out of
// pages/ProfilePage.tsx; the state lives in hooks/useStorefront.

type Props = {
  openSheet: string | null;
  onClose: () => void;
  profile: User;
  currency: PayinCurrency;
  avatarPreview: string | null;
  avatarSrc: string;
  fieldStatus: Record<string, SaveState>;
  storefront: Storefront;
};

export default function StorefrontSheets({ openSheet, onClose, profile, currency, avatarPreview, avatarSrc, fieldStatus, storefront }: Props) {
  const {
    shopAutoAdd,
    setShopAutoAdd,
    setlistPriceInput,
    setlistPadOpen,
    setSetlistPadOpen,
    setlistDraft,
    setSetlistDraft,
    setlistPriceCardRef,
    setlistEnabled,
    storefrontV2,
    showSetlistPreview,
    setShowSetlistPreview,
    setShelfCount,
    saveSetlistPrice,
    commitSetlistPad,
    toggleSetlistSale,
  } = storefront;
  return (
    <>
      {/* ── Storefront drawers — Coming-soon stubs ── */}
      <BottomSheet open={openSheet === "merch"} title={t("storefrontSheets.sellMerch")} icon="🧢" onClose={onClose}>
        <div className="profile-storefront-note">{t("storefrontSheets.sellShirtsHatsMore")}</div>
      </BottomSheet>

      {/* Storefront v2 rides the TALL sheet (founder pick 1B):
          the NumberPad is pinned to the bottom of the screen, so at the
          default 86% it landed on the Custom amount being typed. The extra
          height is what gives the price card somewhere to scroll to. */}
      <BottomSheet
        open={openSheet === "setlists"}
        title={t("storefrontSheets.sellPlaylists")}
        icon={PROFILE_ICONS.playlists}
        onClose={onClose}
        tall={storefrontV2}
      >
        {(() => {
          const chips = setlistChipsFor(profile.pricePerRequest, currency);
          const saved = Number(setlistPriceInput);
          // 0 is a REAL price (free, founder decision) — "has a price"
          // must test for a finite number, never for truthiness, or a free
          // setlist reads as "never priced" and the chips all go dark.
          const hasSaved = setlistPriceInput !== "" && Number.isFinite(saved) && saved >= 0;
          const isFree = hasSaved && saved === 0;
          const chipOn = (c: number) => hasSaved && Math.abs(saved - c) < 0.005;
          const isCustom = hasSaved && !isFree && !chips.some(chipOn);
          return (
            <>
              {/* v2 (founder 4B-looks-like-C): ONE card — "Sell
                  Tonight's set" + toggle in the header, chips grow in
                  beneath when it's ON. No PRICE label, no restating
                  sentence. v1 (flag off) keeps the shipped two-card
                  layout untouched. */}
              {storefrontV2 ? (
                <div className="setlist-card" ref={setlistPriceCardRef}>
                  <div className="setlist-live-head">
                    <span
                      className={`setlist-livedot${setlistEnabled ? "" : " setlist-livedot--off"}`}
                      aria-hidden
                    />
                    <span className="setlist-live-t">{t("storefrontSheets.sellTonightSSet")}</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={setlistEnabled}
                      aria-label={t("storefrontSheets.sellTonightSSet2")}
                      className={`profile-toggle setlist-head-toggle ${setlistEnabled ? "on" : ""}`}
                      onClick={toggleSetlistSale}
                    >
                      <span className="profile-toggle-knob" />
                    </button>
                  </div>
                  {setlistEnabled ? (
                    <>
                      <div className="setlist-chips setlist-chips--headless">
                        {chips.map((c) => (
                          <button
                            key={c}
                            type="button"
                            className={`setlist-chip${chipOn(c) ? " on" : ""}`}
                            onClick={() => saveSetlistPrice(c)}
                          >
                            {formatAmountForCopy(c, currency)}
                          </button>
                        ))}
                        <button
                          type="button"
                          className={`setlist-chip setlist-chip--custom${isCustom || isFree || setlistPadOpen ? " on" : ""}`}
                          onClick={() => {
                            setSetlistDraft("");
                            setSetlistPadOpen(true);
                            // Pad mounts fixed at the bottom — draw the card
                            // up above it so the amount stays in view.
                            setTimeout(() => {
                              setlistPriceCardRef.current?.scrollIntoView({
                                block: "start",
                                behavior: "smooth",
                              });
                            }, 60);
                          }}
                        >
                          {setlistPadOpen && setlistDraft
                            ? `${!SUFFIX_CUR.has(currency) ? currencySymbol(currency) : ""}${setlistDraft}${SUFFIX_CUR.has(currency) ? ` ${currencySymbol(currency)}` : ""}`
                            : isFree
                            ? t("storefrontSheets.free")
                            : isCustom
                            ? formatAmountForCopy(saved, currency)
                            : t("storefrontSheets.custom")}
                        </button>
                      </div>
                      <div className="setlist-sub">
                        {isFree
                          ? t("storefrontSheets.fansRequestItYou")
                          : t("storefrontSheets.fansRequestItYou2")}
                      </div>
                    </>
                  ) : (
                    <div className="setlist-sub setlist-sub--off">
                      {t("storefrontSheets.fansCanTBuy")}
                    </div>
                  )}
                  <FieldStatus status={fieldStatus.setlistSale} />
                  <FieldStatus status={fieldStatus.setlistPrice} />
                </div>
              ) : (
                <>
              <div className="setlist-card">
                <div className="setlist-toggle-row">
                  <div className="setlist-punch">{t("storefrontSheets.allowFansToPurchase")}</div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={setlistEnabled}
                    aria-label={t("storefrontSheets.allowFansToPurchase")}
                    className={`profile-toggle ${setlistEnabled ? "on" : ""}`}
                    onClick={toggleSetlistSale}
                  >
                    <span className="profile-toggle-knob" />
                  </button>
                </div>
                <FieldStatus status={fieldStatus.setlistSale} />
              </div>

              {/* Price — three chips scaled off the request price + Custom. */}
              <div className="setlist-card" ref={setlistPriceCardRef}>
                <div className="setlist-lbl">{t("storefrontSheets.price")}</div>
                <div className="setlist-chips">
                  {chips.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`setlist-chip${chipOn(c) ? " on" : ""}`}
                      onClick={() => saveSetlistPrice(c)}
                    >
                      {formatAmountForCopy(c, currency)}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`setlist-chip setlist-chip--custom${isCustom || isFree || setlistPadOpen ? " on" : ""}`}
                    onClick={() => {
                      setSetlistDraft("");
                      setSetlistPadOpen(true);
                      // Pad mounts fixed at the bottom — draw the card up
                      // above it so the amount stays in view while typing.
                      setTimeout(() => {
                        setlistPriceCardRef.current?.scrollIntoView({
                          block: "start",
                          behavior: "smooth",
                        });
                      }, 60);
                    }}
                  >
                    {setlistPadOpen && setlistDraft
                      ? `${!SUFFIX_CUR.has(currency) ? currencySymbol(currency) : ""}${setlistDraft}${SUFFIX_CUR.has(currency) ? ` ${currencySymbol(currency)}` : ""}`
                      : isFree
                      ? t("storefrontSheets.free")
                      : isCustom
                      ? formatAmountForCopy(saved, currency)
                      : t("storefrontSheets.custom")}
                  </button>
                </div>
                <div className="setlist-sub">{t("storefrontSheets.fansCanBidHigher")}</div>
                <FieldStatus status={fieldStatus.setlistPrice} />
              </div>
                </>
              )}

              {/* The shelf — up to three saved playlists (Storefront v2).
                  Only mounts behind the server's capability flag; with it
                  off this whole feature does not exist for the DJ. */}
              {storefrontV2 && (
                <ShopPlaylists
                  currency={currency}
                  requestPrice={profile.pricePerRequest}
                  liveOn={setlistEnabled}
                  livePrice={hasSaved ? saved : null}
                  onOpenLivePreview={() => setShowSetlistPreview(true)}
                  autoOpenAdd={shopAutoAdd}
                  onAutoOpenConsumed={() => setShopAutoAdd(false)}
                  onShelfChange={setShelfCount}
                />
              )}

              {/* How it works — founder copy verbatim. v2 says the same
                  thing in two shorter places (the tonight's-set subline
                  above and the shelf's own line), so the card would only
                  be restating them. */}
              {!storefrontV2 && (
              <div className="setlist-card">
                <div className="setlist-punch">
                  <T k="storefrontSheets.someoneBuysYouUpload" tags={{ 0: (c) => <span className="setlist-g">{c}</span> }} />
                </div>
                <div className="setlist-sub">
                  {t("storefrontSheets.onlyUploadWhenSomeone")}
                </div>
              </div>
              )}

              {/* The excitement moment — sample of the fan's delivery page. */}
              <button
                type="button"
                className="setlist-preview-strip"
                onClick={() => setShowSetlistPreview(true)}
              >
                <img src="https://i.scdn.co/image/ab67616d0000b27306d56b057cce5797538a16d5" alt="" />
                <img src="https://i.scdn.co/image/ab67616d0000b273adfb5909ec66db5fbb4d06c8" alt="" />
                <img src="https://i.scdn.co/image/ab67616d0000b273e2e352d89826aef6dbd5ff8f" alt="" />
                <span className="setlist-preview-label">{t("storefrontSheets.seeWhatFansGet")}</span>
                <span className="setlist-preview-chev" aria-hidden>›</span>
              </button>
            </>
          );
        })()}
        <NumberPad
          open={setlistPadOpen}
          value={setlistDraft}
          onChange={setSetlistDraft}
          onDone={commitSetlistPad}
          allowDecimal
          withBackdrop
          maxLength={6}
        />
      </BottomSheet>

      {/* "See what fans get" — a static sample of the delivered setlist
          page (real artwork, decorative buttons). The live page fans get
          is /playlist/:receiptId. */}
      {showSetlistPreview && (
        <div className="setlist-preview-modal" onClick={() => setShowSetlistPreview(false)}>
          <div className="setlist-preview-page" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="setlist-preview-close"
              onClick={() => setShowSetlistPreview(false)}
              aria-label={t("storefrontSheets.closePreview")}
            >
              ✕
            </button>
            {/* It's a picture of the fan's page, so its buttons do nothing:
                the label says so, for talent and for App Review. */}
            <span className="setlist-preview-sample">{t("storefrontSheets.sample")}</span>
            {/* The reveal (founder decision): what fans see the moment they
                buy — the sealed record with YOUR ribbon — then what the
                upload turns it into. */}
            <div className="setlist-preview-sealed">
              <div className="setlist-preview-sealed-art">
                <SetlistSealedArt djName={profile.name} djAvatarUrl={avatarPreview ? avatarSrc : null} withMarks />
              </div>
              <div className="setlist-preview-cap">{t("storefrontSheets.theMomentTheyBuy")}</div>
            </div>
            <div className="setlist-preview-arrow" aria-hidden>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#24C961" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 4v14M6 12l6 6 6-6" />
              </svg>
            </div>
            <div className="setlist-preview-cap setlist-preview-cap--after">{t("storefrontSheets.afterYourUpload")}</div>
            <div className="setlist-preview-mosaic">
              <img src="https://i.scdn.co/image/ab67616d0000b27306d56b057cce5797538a16d5" alt="" />
              <img src="https://i.scdn.co/image/ab67616d0000b273adfb5909ec66db5fbb4d06c8" alt="" />
              <img src="https://i.scdn.co/image/ab67616d0000b273e2e352d89826aef6dbd5ff8f" alt="" />
              <img src="https://i.scdn.co/image/ab67616d0000b27311b21e8b868800918d21bb77" alt="" />
            </div>
            <div className="setlist-preview-title">
              {profile.name ? t("storefrontSheets.augustSetlistNamed", { name: String(profile.name) }) : t("storefrontSheets.augustSetlist")}
            </div>
            <div className="setlist-preview-meta">
              {t("storefrontSheets.songsPmAm", { name: profile.name })}
            </div>
            <div className="setlist-preview-btn setlist-preview-btn--spotify">
              {t("storefrontSheets.addToSpotify")}
            </div>
            <div className="setlist-preview-btn setlist-preview-btn--apple">
              {t("storefrontSheets.addToAppleMusic")}
            </div>
            <div className="setlist-preview-trk">
              <img src="https://i.scdn.co/image/ab67616d0000b273ba5db46f4b838ef6027e6f96" alt="" />
              <div className="setlist-preview-trk-meta">
                <div className="setlist-preview-trk-t">{t("storefrontSheets.theWeekendFunkWav")}</div>
                <div className="setlist-preview-trk-a">{t("storefrontSheets.szaCalvinHarris")}</div>
              </div>
            </div>
            <div className="setlist-preview-trk">
              <img src="https://i.scdn.co/image/ab67616d0000b27345680a4a57c97894490a01c1" alt="" />
              <div className="setlist-preview-trk-meta">
                <div className="setlist-preview-trk-t">{t("storefrontSheets.itGoesLikeNanana")}</div>
                <div className="setlist-preview-trk-a">{t("storefrontSheets.peggyGou")}</div>
              </div>
            </div>
            <div className="setlist-preview-trk">
              <img src="https://i.scdn.co/image/ab67616d0000b273dd5bb7e368902181862f4ebe" alt="" />
              <div className="setlist-preview-trk-meta">
                <div className="setlist-preview-trk-t">{t("storefrontSheets.oneKiss")}</div>
                <div className="setlist-preview-trk-a">{t("storefrontSheets.calvinHarrisDuaLipa")}</div>
              </div>
            </div>
            <div className="setlist-preview-more">{t("storefrontSheets.more")}</div>
          </div>
        </div>
      )}
    </>
  );
}
